import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useDhsFlag } from 'utils/featureFlags';
import {
  DHS_REGION4_EVENT_SUMMARY,
  DHS_REGION4_FACILITIES,
  DHS_REGION4_LINKED_EVENT_NAME,
  DHS_REGION4_ORG_EVENT_DEMO_KEY,
  DHS_REGION4_RESOURCES_COMMITTED,
  DHS_REGION4_THRESHOLD_DESCRIPTION,
  type DhsRegion4Facility,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/dhsRegion4ThreatNotification';
import { orgEventKeys, type OrgEvent } from 'hooks/orgEvents/orgEventHooks';

const DHS_REGION4_EVENT_REGION = 'FEMA Region IV';

const ensureLocks = new Map<string, Promise<unknown>>();

function dhsRegion4Centroid(sites: DhsRegion4Facility[]): {
  lon: number;
  lat: number;
} {
  if (sites.length === 0) {
    return { lon: -81.5, lat: 31.5 };
  }
  const lon = sites.reduce((sum, f) => sum + f.lon, 0) / sites.length;
  const lat = sites.reduce((sum, f) => sum + f.lat, 0) / sites.length;
  return { lon, lat };
}

async function patchDhsRegion4OrgEventIfStale(
  existing: OrgEvent,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<OrgEvent> {
  const needsPatch =
    existing.name !== DHS_REGION4_LINKED_EVENT_NAME ||
    existing.summary !== DHS_REGION4_EVENT_SUMMARY ||
    existing.resources_committed !== DHS_REGION4_RESOURCES_COMMITTED ||
    existing.region !== DHS_REGION4_EVENT_REGION ||
    existing.type !== 'Tropical Storm' ||
    existing.threshold_description !== DHS_REGION4_THRESHOLD_DESCRIPTION;
  if (!needsPatch) {
    return existing;
  }

  const patched = await axios.patch<OrgEvent>(
    `${config.apiGateway.org_events}/${existing.id}`,
    {
      name: DHS_REGION4_LINKED_EVENT_NAME,
      summary: DHS_REGION4_EVENT_SUMMARY,
      resources_committed: DHS_REGION4_RESOURCES_COMMITTED,
      region: DHS_REGION4_EVENT_REGION,
      type: 'Tropical Storm',
      threshold_description: DHS_REGION4_THRESHOLD_DESCRIPTION,
    }
  );
  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(existing.group_guid),
  });
  return patched.data;
}

async function ensureDhsRegion4OrgEventForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<OrgEvent> {
  const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
    params: { groupGuid },
  });
  const existing = (resp.data ?? []).find(
    (event) => event.demo_key === DHS_REGION4_ORG_EVENT_DEMO_KEY
  );
  if (existing) {
    return patchDhsRegion4OrgEventIfStale(existing, queryClient);
  }

  const { lon, lat } = dhsRegion4Centroid(DHS_REGION4_FACILITIES);
  const created = await axios.post<OrgEvent>(config.apiGateway.org_events, {
    group_guid: groupGuid,
    name: DHS_REGION4_LINKED_EVENT_NAME,
    type: 'Tropical Storm',
    status: 'Active',
    severity: 'High',
    region: DHS_REGION4_EVENT_REGION,
    business_unit: 'DHS',
    summary: DHS_REGION4_EVENT_SUMMARY,
    resources_committed: DHS_REGION4_RESOURCES_COMMITTED,
    lon,
    lat,
    started_at: '2024-09-24T12:00:00.000Z',
    creation_kind: 'threshold',
    created_by_user_guid: userGuid,
    threshold_description: DHS_REGION4_THRESHOLD_DESCRIPTION,
    demo_key: DHS_REGION4_ORG_EVENT_DEMO_KEY,
  });

  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(groupGuid),
  });

  return created.data;
}

export function useEnsureDhsRegion4OrgEvent() {
  const dhsEnabled = useDhsFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!dhsEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `dhs-org-event:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureDhsRegion4OrgEventForGroup({
        groupGuid,
        userGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure DHS Region 4 org event', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [dhsEnabled, groupGuid, userGuid, queryClient]);
}
